import React from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { countDriverResolvedTickets } from '../../../common/utils/ticketList/countDriverTickets';
import { calculateTicketCount } from '../../../common/utils/serviceRuns/calculateTicketCount';
import { getServiceTypeName } from '../../../common/utils/serviceRuns/getServiceTypeName';
import { analyseCoinMech } from '../../../common/utils/vending-utils';
import { getFullMachineName } from '../../../common/utils/machine';
import { MACHINE_SERVICE_TYPES, RESTOCK_TYPE, STATUS_NAMES, STATUS, POS_TYPE } from '../../../common/constants';
import { addressToString } from '../../../common/utils/address';
import { useUserContext } from '../../../common/utils/context';
import { getPickListStatusLabel } from '../../../common/utils/pickList/getPickListStatusLabel';
import { Icon, ListItem } from '../../components';
import Text from '../../components/text/Text';
import { Tag } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { isLegacyMobileUi } from '../../utils/legacyMobileUi';
import { useDriverDayStopSelector } from '../driver/DriverDayProvider';
import { DRIVER_ROUTE, KEEP_OPENER_TAB_PARAM } from '../driver/driverRoutes';

// Use short, driver-friendly labels to avoid wrapping on narrow screens.
export const RESTOCK_TYPE_LABELS = {
	[RESTOCK_TYPE.ALL_PRODUCTS]: 'All products',
	[RESTOCK_TYPE.FRESH_FOOD_ONLY]: 'Fresh food',
	[RESTOCK_TYPE.EXCLUDE_FRESH_FOOD]: 'Non-fresh food',
};

const ServiceRunDetailMachineListItem = ({
	item, // This is the service docket
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const navigation = useNavigation();
	const selectDriverDayStop = useDriverDayStopSelector();
	const { machine, inventoryPicked, inventoryPartiallyPicked, pickList, cashBag, tub } = item;

	const { user } = useUserContext();
	const { tenantSettings } = user;
	const { tubesDetail } = machine.coinMechDetail;
	const { mechRequiresFill } = analyseCoinMech(tubesDetail, machine.coinMech, tenantSettings, machine);

	const machineName = getFullMachineName(machine, machine.place, machine.location);

	// The redesigned stop screen opens every stop (it loads stops outside the
	// user's driver day itself); only the legacy UI keeps the classic docket
	// detail. A stop of the user's own day also becomes their current stop;
	// any other stop keeps the opener's tab (All Runs) on the bottom bar.
	const isLegacyUi = isLegacyMobileUi(user);
	const onPress = () => {
		const isInDriverDay = !isLegacyUi && selectDriverDayStop(item.id);
		navigation.push(isLegacyUi ? 'DocketDetailView' : DRIVER_ROUTE.STOP, {
			docketId: item.id,
			machineName,
			machineId: machine.id,
			machineType: machine.type,
			isMarket: machine.type === POS_TYPE.MARKET,
			...(!isLegacyUi && !isInDriverDay ? { [KEEP_OPENER_TAB_PARAM]: true } : {}),
		});
	};

	const ticketCount = calculateTicketCount(item, tenantSettings);

	const getTicketLabel = () => {

		if (!ticketCount) {
			return null;
		}

		const resolvedTicketCount = countDriverResolvedTickets(item.ticketList.driverItems, tenantSettings);

		return (
			<Tag
				label={`TICKETS: ${resolvedTicketCount}/${ticketCount}`}
				tone={resolvedTicketCount === ticketCount ? 'success' : 'neutral'}
			/>
		);
	};

	const getInventoryPickedLabel = () => {
		const pickListStatusLabel = getPickListStatusLabel({
			inventoryPicked,
			inventoryPartiallyPicked,
			isAnyItemPicked: pickList?.isAnyItemPicked,
		});
		if (pickListStatusLabel === 'Pending') {
			return null;
		}

		return (
			<Tag
				label={pickListStatusLabel.toUpperCase()}
				tone={inventoryPicked ? 'success' : 'warning'}
			/>
		);
	};

	const getPhotoLabel = () => {

		if (!item.requirePhoto) {
			return null;
		}

		return (
			<Tag
				label="PHOTO"
				tone={item.photoTaken ? 'success' : 'neutral'}
			/>
		);
	};

	const serviceTypeName = getServiceTypeName(item, ticketCount);
	const restockTypeLabel = RESTOCK_TYPE_LABELS[item.machineRestockType];
	const shouldShowMachineKey = machine.type !== POS_TYPE.MARKET && Boolean(machine.machineKey);

	const address = machine.location ? addressToString(machine.location.addressObj) : null;
	const requiresAccessPass = Boolean(machine.location?.requiresAccessPass);
	const requiresSignIn = Boolean(machine.location?.requiresSignIn);
	const signInInstructions = machine.location?.signInInstructions?.trim();
	const showCoinsRequired = Boolean(machine.hasCoinMech && mechRequiresFill && !machine.isOffline);

	return (
		<ListItem onPress={onPress} style={styles.item}>
			<View style={styles.topRow}>
				<View style={styles.tagRow}>
					<Tag
						label={STATUS_NAMES[item.status].toUpperCase()}
						tone={item.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					/>
					{ getInventoryPickedLabel() }
					{ getTicketLabel() }
					{ getPhotoLabel() }
				</View>
				<View style={styles.tagRow}>
					{
						tenantSettings.enableTubsFieldOnDockets && Boolean(tub) && (
							<Tag label={`TUB: ${tub}`} style={styles.outlineTag} />
						)
					}
					{
						Boolean(cashBag) && (
							<Tag label={`CASH BAG: ${cashBag}`} style={styles.outlineTag} />
						)
					}
				</View>
			</View>
			<Text style={styles.machineName}>
				{machineName}
			</Text>
			{ address ? <Text style={styles.machineAttribute}>{address}</Text> : null}
			<View style={styles.tagRow}>
				{
					item.machine.isOffline && (
						<Tag label="OFFLINE" style={styles.outlineTag} />
					)
				}
				<Tag label={serviceTypeName.toUpperCase()} style={styles.outlineTag} />
				{
					item.machineServiceType !== MACHINE_SERVICE_TYPES.REPAIR && Boolean(restockTypeLabel) && (
						<Tag label={restockTypeLabel.toUpperCase()} style={styles.outlineTag} />
					)
				}
				{
					item.collectCash && (
						<Tag label="COLLECT CASH" style={styles.outlineTag} />
					)
				}
			</View>
			<View style={styles.attributeRow}>
				{
					machine.machineModel
						? (
							<Text style={styles.machineAttribute}>
								{machine.machineModel.name}
							</Text>
						)
						: null
				}

				{
					machine.wrap?.name
						? (
							<Text style={styles.machineAttribute}>
								{machine.wrap?.name}
							</Text>
						)
						: null
				}

				{
					machine.type === POS_TYPE.MACHINE && machine.machineType?.name
						? (
							<Text style={styles.machineAttribute}>
								{machine.machineType.name}
							</Text>
						)
						: null
				}

				{
					shouldShowMachineKey
						? (
							<View style={styles.keyRow}>
								<Icon
									name="key"
									size={14}
									color={colors.iconMuted}
								/>
								<Text style={styles.machineAttribute}>
									{ machine.machineKey.name }
								</Text>
							</View>
						)
						: null
				}
			</View>
			{
				(requiresAccessPass || requiresSignIn || showCoinsRequired) && (
					<View style={styles.tagRow}>
						{
							requiresAccessPass && (
								<Tag tone="warning" label="Requires access pass" />
							)
						}
						{
							requiresSignIn && (
								<Tag tone="warning" label="Site sign-in required" />
							)
						}
						{
							showCoinsRequired && (
								<Tag tone="warning" label="Coins required" />
							)
						}
					</View>
				)
			}
			{requiresSignIn && signInInstructions && (
				<Text style={styles.machineAttribute}>
					{signInInstructions}
				</Text>
			)}
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: 6,
	},
	topRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: space.sm,
	},
	tagRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.xs,
		flexShrink: 1,
	},
	outlineTag: {
		backgroundColor: colors.card,
	},
	machineName: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	attributeRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		columnGap: space.sm,
	},
	keyRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
	},
	machineAttribute: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default ServiceRunDetailMachineListItem;
